'use client';

import Icon from '@/components/ui/AppIcon';

import useScrollReveal from '@/hooks/useScrollReveal';

interface Principle {
  title: string;
  description: string;
  icon: string;
  accentColor: string;
  image: string;
  imageAlt: string;
}

export default function Philosophy() {
  const { ref: sectionRef, isVisible: sectionVisible } = useScrollReveal<HTMLElement>();

  const values = [
  {
    title: 'Structure Creates Freedom',
    description: 'Good systems don\'t constrain you—they create space to focus on what actually matters. Automation handles the repetitive work. Strategy guides the big decisions. Infrastructure enables growth without chaos. Freedom comes from having the right foundation, not from winging it.',
    icon: 'CogIcon',
    accentColor: 'brand-teal',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18fd79417-1772545964474.png",
    imageAlt: 'Organized workflow system and automation dashboard'
  },
  {
    title: 'Boutique by Design',
    description: 'We stay small because scale dilutes quality. Every client gets direct access to me, not an account manager. Every project gets strategic attention, not a templated process. No junior team members learning on your dime. This is intentional—we\'d rather do fewer projects well than more projects poorly.',
    icon: 'UserGroupIcon',
    accentColor: 'brand-orange',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_172d684ea-1777416626165.png",
    imageAlt: 'Small team collaborating closely on client project'
  },
  {
    title: 'Selective Partnerships',
    description: 'We work with founders ready to invest in real infrastructure, not shortcuts. Not everyone is there yet, and that\'s okay. But if you\'re done competing on price and ready to build something that lasts, we should talk. We turn down more projects than we take on.',
    icon: 'HandshakeIcon',
    accentColor: 'brand-green',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f5f7b7e9-1784915379358.png",
    imageAlt: 'Professional handshake and partnership agreement'
  },
  {
    title: 'No Fake Metrics',
    description: 'We don\'t inflate numbers or promise viral growth. We build foundations that compound over time. Real outcomes you can measure—more leads, better conversion, time saved. Not vanity metrics that look good in a report but don\'t move your business forward.',
    icon: 'ChartBarIcon',
    accentColor: 'brand-plum',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13e939552-1784915380085.png",
    imageAlt: 'Real analytics dashboard showing genuine business metrics'
  }];


  return (
    <section ref={sectionRef} className="py-12 sm:py-16 md:py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        <div className={`text-center mb-10 sm:mb-12 md:mb-16 transition-all duration-1000 ${sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="font-headline text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-brand-plum mb-4 sm:mb-6 font-bold">
            How We Work
          </h2>
          <p className="font-body text-lg sm:text-xl md:text-2xl text-text-secondary max-w-3xl mx-auto px-4">
            Four principles that guide every decision we make.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {values.map((value, index) => {
            const { ref: cardRef, isVisible: cardVisible } = useScrollReveal<HTMLDivElement>();
            return (
              <div
                key={index}
                ref={cardRef}
                className={`group text-center p-6 sm:p-8 bg-brand-offwhite hover:bg-white transition-all duration-500 transform hover:-translate-y-2 hover:shadow-2xl shadow-lg ${cardVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 150}ms` }}>
                
                <div className="space-y-4 sm:space-y-6">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-${value.accentColor} flex items-center justify-center`}>
                      <Icon name={value.icon} size={24} className="text-white sm:w-8 sm:h-8" />
                    </div>
                    <div className={`text-4xl sm:text-5xl md:text-6xl font-headline text-${value.accentColor} font-bold`}>
                      {String(index + 1).padStart(2, '0')}
                    </div>
                  </div>
                  
                  <h3 className={`font-headline text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-brand-plum font-bold border-l-4 border-${value.accentColor} pl-4 sm:pl-6 text-left`}>
                    {value.title}
                  </h3>
                  <p className="font-body text-base sm:text-lg md:text-xl text-text-secondary leading-relaxed pl-4 sm:pl-6 text-left">
                    {value.description}
                  </p>
                </div>
              </div>);

          })}
        </div>
      </div>
    </section>);

}